/**
 * ThreadSectionHeader — one section header for the rail and the sheet
 * (2026-09-09): the label toggles collapse (caret + glyph + name + count), an
 * inline rename swaps in for the name, and a ⋯ ghost opens the section's
 * actions in a body-portaled dark menu — rename, move up / down, collapse,
 * a new category, delete (custom sections only; a built-in can be renamed
 * and moved but never removed), and "manage sections…" — the one list that
 * shows EVERY section, the empty ones the rail hides included
 * (ThreadSectionsManager, 2026-09-09). Move up / down here are relative to
 * the sections the viewer can SEE (`visibleSectionIds`), so a move is never
 * swallowed by a hidden empty neighbour. The header is also a drop target
 * for a dragged row (`useThreadDrag`).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { SidebarSectionCount, SidebarSectionHeader } from './Routines.styles';
import { CategoryFolderGlyph, ThreadTypeGlyph } from './ThreadTypeGlyph';
import { sectionGlyphKind, type ThreadSection } from './threadSections';
import { threadListCopy } from './threadListCopy';
import { canOrganizeThreads, useAnchoredMenu, type ThreadOrganizeApi } from './threadOrganize';
import { ThreadSectionsManager } from './ThreadSectionsManager';
import { RailGhostIconButton, RailInlineInput, RailMenu, RailMenuHeading, RailMenuInput, RailMenuItem } from './organizeStyles';
import { DEFAULT_SECTION_LABELS, THREAD_CATEGORY_NAME_MAX } from '../../types/threadCategories';

/** The sections manager is wider than a menu: four controls beside a name. */
export const SECTIONS_MANAGER_WIDTH = 280;

export interface ThreadSectionHeaderProps {
  section: ThreadSection<unknown>;
  /** The stable DOM hook value (sidebarSectionHook) and which attribute carries it. */
  hook: string;
  hookAttr: 'data-sidebar-section' | 'data-sheet-section';
  organize?: ThreadOrganizeApi | null;
  /** Position among the VISIBLE sections (move up / down availability). */
  index: number;
  total: number;
  /** The visible section ids in display order — a move is relative to the visible neighbour. */
  visibleSectionIds?: readonly string[];
  /** EVERY section (empty ones included) for "manage sections…"; the item is offered only when given. */
  manageSections?: ReadonlyArray<ThreadSection<unknown>>;
  dropActive?: boolean;
  dropHandlers?: React.HTMLAttributes<HTMLDivElement>;
}

const Caret: React.FC<{ open: boolean }> = ({ open }) => (
  <svg viewBox="0 0 12 12" width={10} height={10} aria-hidden="true" style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.12s ease', flex: 'none' }}>
    <path d="M4 2.5 L8 6 L4 9.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Dots: React.FC = () => (
  <svg viewBox="0 0 12 12" aria-hidden="true">
    <circle cx="2.5" cy="6" r="1.2" fill="currentColor" />
    <circle cx="6" cy="6" r="1.2" fill="currentColor" />
    <circle cx="9.5" cy="6" r="1.2" fill="currentColor" />
  </svg>
);

export const ThreadSectionHeader: React.FC<ThreadSectionHeaderProps> = ({
  section, hook, hookAttr, organize, index, total, visibleSectionIds, manageSections, dropActive, dropHandlers,
}) => {
  const lang = organize?.lang ?? 'en';
  const copy = threadListCopy(lang);
  const collapsed = !!organize?.collapsed.has(section.categoryId);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menu = useAnchoredMenu(buttonRef);
  // The sections manager rides the same anchored-menu contract (position from
  // the ⋯, focus-first, Escape refocuses it, an outside press closes).
  const manager = useAnchoredMenu(buttonRef, { width: SECTIONS_MANAGER_WIDTH });
  const moveOptions = visibleSectionIds ? { among: visibleSectionIds } : undefined;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(section.label);
  const [taken, setTaken] = useState(false);
  const [newName, setNewName] = useState('');
  const [newOpen, setNewOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) setDraft(section.label);
  }, [section.label, editing]);

  const startRename = useCallback(() => {
    menu.close(false);
    setTaken(false);
    setDraft(section.label);
    setEditing(true);
  }, [menu, section.label]);

  const commitRename = useCallback(async () => {
    if (!organize) return;
    const clean = draft.trim();
    if (!clean || clean === section.label) {
      setEditing(false);
      return;
    }
    const ok = await organize.renameCategory(section.categoryId, clean);
    if (ok) setEditing(false);
    else {
      setTaken(true);
      inputRef.current?.focus();
    }
  }, [organize, draft, section.label, section.categoryId]);

  const submitNew = useCallback(async () => {
    if (!organize) return;
    const name = newName.trim();
    if (!name) return;
    const id = await organize.createCategory(name);
    if (id) {
      setNewName('');
      setNewOpen(false);
      menu.close(true);
    }
  }, [organize, newName, menu]);

  const canOrganize = canOrganizeThreads(organize);
  const isCustom = section.kind === 'category';
  /**
   * What a DEFAULT section is for (2026-09-13). Renaming "group threads" to
   * "triage" surprised its owner because nothing anywhere said new group
   * threads would keep arriving in it — the section's meaning was invisible.
   * Quiet at rest, on the label itself; a custom section has no such meaning
   * and gets none.
   */
  const defaultHome = section.builtin
    ? copy.sectionDefaultHome(DEFAULT_SECTION_LABELS[lang][section.builtin])
    : '';
  const glyph = isCustom
    ? <CategoryFolderGlyph active size={12} />
    : <ThreadTypeGlyph kind={sectionGlyphKind(section.kind)} active size={12} />;

  return (
    <SidebarSectionHeader
      {...{ [hookAttr]: hook }}
      data-section-id={section.categoryId}
      data-section-collapsed={collapsed ? 'true' : undefined}
      data-drop-active={dropActive ? 'true' : undefined}
      style={dropActive ? { background: 'rgba(198, 167, 94, 0.14)', borderRadius: 8, outline: '1px dashed rgba(198, 167, 94, 0.6)' } : undefined}
      {...(dropHandlers ?? {})}
    >
      {editing && canOrganize && organize ? (
        <>
          {glyph}
          <RailInlineInput
            ref={inputRef}
            autoFocus
            data-section-rename-input=""
            aria-label={copy.renameSection}
            aria-invalid={taken || undefined}
            title={taken ? copy.nameTaken : undefined}
            placeholder={copy.sectionNamePlaceholder}
            maxLength={THREAD_CATEGORY_NAME_MAX}
            value={draft}
            onChange={(e) => { setDraft(e.target.value); setTaken(false); }}
            onBlur={() => { void commitRename(); }}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') { e.preventDefault(); void commitRename(); }
              if (e.key === 'Escape') { e.preventDefault(); setEditing(false); setDraft(section.label); }
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </>
      ) : canOrganize ? (
        <button
          type="button"
          data-section-toggle=""
          aria-expanded={!collapsed}
          aria-label={`${section.label} · ${section.rows.length} — ${collapsed ? copy.expand : copy.collapse}${defaultHome ? ` · ${defaultHome}` : ''}`}
          title={defaultHome || undefined}
          onClick={() => organize!.setCollapsed(section.categoryId, !collapsed)}
          onDoubleClick={(e) => { e.preventDefault(); startRename(); }}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0, padding: 0, margin: 0,
            background: 'transparent', border: 'none', color: 'inherit', font: 'inherit', letterSpacing: 'inherit',
            cursor: 'pointer', textAlign: 'left',
          }}
        >
          <Caret open={!collapsed} />
          {glyph}
          <span data-section-label="" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{section.label}</span>
          <SidebarSectionCount>· {section.rows.length}</SidebarSectionCount>
        </button>
      ) : (
        <>
          {glyph}
          <span data-section-label="" title={defaultHome || undefined}>{section.label}</span>
          <SidebarSectionCount>· {section.rows.length}</SidebarSectionCount>
        </>
      )}
      {canOrganize && !editing && (
        <RailGhostIconButton
          ref={buttonRef}
          type="button"
          data-section-menu-btn=""
          aria-haspopup="menu"
          aria-expanded={menu.open}
          aria-label={copy.sectionActions(section.label)}
          title={copy.sectionActions(section.label)}
          style={{ marginLeft: 'auto' }}
          onClick={(e) => { e.stopPropagation(); menu.toggle(); }}
        >
          <Dots />
        </RailGhostIconButton>
      )}
      {canOrganize && menu.open && typeof document !== 'undefined' && createPortal(
        <RailMenu
          ref={menu.menuRef}
          role="menu"
          aria-label={copy.sectionActions(section.label)}
          data-section-menu=""
          data-prest0n-portal="true"
          $left={menu.pos.left}
          $top={menu.pos.top}
          onClick={(e) => e.stopPropagation()}
        >
          <RailMenuHeading>{section.label}</RailMenuHeading>
          <RailMenuItem type="button" role="menuitem" data-section-action="rename" onClick={startRename}>{copy.renameSection}</RailMenuItem>
          <RailMenuItem type="button" role="menuitem" data-section-action="up" disabled={index <= 0} onClick={() => { void organize!.moveCategory(section.categoryId, 'up', moveOptions); menu.close(true); }}>{copy.moveUp}</RailMenuItem>
          <RailMenuItem type="button" role="menuitem" data-section-action="down" disabled={index >= total - 1} onClick={() => { void organize!.moveCategory(section.categoryId, 'down', moveOptions); menu.close(true); }}>{copy.moveDown}</RailMenuItem>
          <RailMenuItem type="button" role="menuitem" data-section-action="collapse" onClick={() => { organize!.setCollapsed(section.categoryId, !collapsed); menu.close(true); }}>{collapsed ? copy.expand : copy.collapse}</RailMenuItem>
          {manageSections && (
            <RailMenuItem type="button" role="menuitem" data-section-action="manage" onClick={() => { menu.close(false); manager.openAt(); }}>{copy.manageSections}</RailMenuItem>
          )}
          {newOpen ? (
            <RailMenuInput
              type="text"
              autoFocus
              data-section-new-category=""
              aria-label={copy.newCategory}
              placeholder={copy.newCategoryPlaceholder}
              maxLength={THREAD_CATEGORY_NAME_MAX}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); void submitNew(); }
              }}
            />
          ) : (
            <RailMenuItem type="button" role="menuitem" data-section-action="new" onClick={() => setNewOpen(true)}>{copy.newCategory}</RailMenuItem>
          )}
          {isCustom && (
            <RailMenuItem type="button" role="menuitem" data-section-action="delete" $danger onClick={() => { void organize!.removeCategory(section.categoryId); menu.close(false); }}>{copy.deleteSection}</RailMenuItem>
          )}
        </RailMenu>,
        document.body,
      )}
      {canOrganize && manageSections && manager.open && typeof document !== 'undefined' && createPortal(
        <ThreadSectionsManager
          organize={organize!}
          sections={manageSections}
          left={manager.pos.left}
          top={manager.pos.top}
          menuRef={manager.menuRef}
          onClose={() => manager.close(true)}
        />,
        document.body,
      )}
    </SidebarSectionHeader>
  );
};

export default ThreadSectionHeader;
